import { useMutation, useQueryClient } from '@tanstack/react-query';
import { equipamentoService } from '../../services/equipamentoService';
import type {
  AnexoAtivo,
  CategoriaAnexoAtivo,
  AlteracaoSituacaoAtivo,
  EquipamentoCreate,
  EquipamentoUpdate,
  ExclusaoAtivo,
} from '../../models/equipamento.types';
import type { AuditoriaAtor } from '../../models/solicitacao.types';
import { SI_EQUIPAMENTOS_QUERY_KEYS } from './useEquipamentoQuery';
import { useToast } from '@/hooks/useToast';

export function useEquipamentoMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: SI_EQUIPAMENTOS_QUERY_KEYS.all });

  const criarEquipamento = useMutation({
    mutationFn: (data: EquipamentoCreate) => equipamentoService.create(data),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao cadastrar equipamento: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const atualizarEquipamento = useMutation({
    mutationFn: (data: EquipamentoUpdate) => equipamentoService.update(data),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao atualizar equipamento: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const alterarSituacao = useMutation({
    mutationFn: (data: AlteracaoSituacaoAtivo) => equipamentoService.alterarSituacao(data),
    onSuccess: () => {
      invalidar();
      toast.success('Situação alterada.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao alterar a situação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const adicionarComentario = useMutation({
    mutationFn: ({ id, texto, autor }: { id: string; texto: string; autor: AuditoriaAtor }) =>
      equipamentoService.adicionarComentario(id, texto, autor),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao adicionar comentário: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const adicionarAnexo = useMutation({
    mutationFn: ({
      id,
      arquivo,
      categoria,
      autor,
      dono,
    }: {
      id: string;
      arquivo: File;
      categoria: CategoriaAnexoAtivo;
      autor: AuditoriaAtor;
      dono?: { id: string; name: string } | null;
    }) => equipamentoService.adicionarAnexo(id, arquivo, categoria, autor, dono ?? null),
    onSuccess: () => {
      invalidar();
      toast.success('Arquivo anexado.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao anexar arquivo: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const removerAnexo = useMutation({
    mutationFn: ({ id, anexo, autor }: { id: string; anexo: AnexoAtivo; autor: AuditoriaAtor }) =>
      equipamentoService.removerAnexo(id, anexo, autor),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao remover anexo: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const excluirAtivo = useMutation({
    mutationFn: (data: ExclusaoAtivo) => equipamentoService.excluir(data),
    onSuccess: () => {
      invalidar();
      toast.success('Ativo excluído. Dá pra restaurar em "Excluídos".');
    },
    onError: (error: any) => {
      toast.error(`Erro ao excluir o ativo: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const restaurarAtivo = useMutation({
    mutationFn: ({ id, ator }: { id: string; ator: AuditoriaAtor }) => equipamentoService.restaurar(id, ator),
    onSuccess: () => {
      invalidar();
      toast.success('Ativo restaurado.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao restaurar o ativo: ${error?.message || 'erro desconhecido'}`);
    },
  });

  return {
    criarEquipamento,
    atualizarEquipamento,
    alterarSituacao,
    adicionarComentario,
    adicionarAnexo,
    removerAnexo,
    excluirAtivo,
    restaurarAtivo,
  };
}
